import { api, withIdempotencyKey } from "@/lib/api/client";

import type {
  Patient,
  PatientFilters,
  PatientListResponse,
  PatientMedicalRecord,
} from "./types";

export interface SummaryConsultation {
  id: string;
  date: string;
  practitioner: string;
  reason: string;
}
export interface SummaryPrescription {
  id: string;
  date: string;
  type: string;
}
export interface SummaryAppointment {
  id: string;
  scheduled_at: string;
  status: string;
  status_display: string;
  reason: string;
}
export interface SummaryPayment {
  id: string;
  date: string;
  amount: string;
  method: string;
  is_refunded: boolean;
}
export interface SummaryBalance {
  total_invoiced: string;
  total_paid: string;
  outstanding: string;
}
export interface TimelineEvent {
  type:
    | "consultation"
    | "prescription"
    | "eye_prescription"
    | "appointment"
    | "sale"
    | "payment";
  date: string;
  label: string;
  ref_id: string;
}
export interface PatientSummary {
  patient_id: string;
  counts: {
    consultations: number;
    prescriptions: number;
    appointments: number;
    payments?: number;
    sales?: number;
  };
  next_appointment: SummaryAppointment | null;
  recent: {
    consultations: SummaryConsultation[];
    prescriptions: SummaryPrescription[];
    appointments: SummaryAppointment[];
    payments?: SummaryPayment[];
  };
  balance: SummaryBalance | null;
  timeline: TimelineEvent[];
  /**
   * Vrai si le patient a des actes ANTÉRIEURS à ceux renvoyés. Le parcours
   * n'est jamais rogné en silence : l'écran doit le signaler.
   */
  timeline_truncated: boolean;
  /**
   * Date depuis laquelle le parcours est COMPLET pour tous les types d'acte.
   * Le back coupe à cette borne pour éviter une période où un type
   * apparaîtrait et un autre serait invisible faute d'avoir été chargé.
   */
  timeline_complete_since: string | null;
}

const buildParams = (filters: PatientFilters): Record<string, string | number | boolean> => {
  const out: Record<string, string | number | boolean> = {};
  if (filters.search) out["search"] = filters.search;
  if (filters.gender) out["gender"] = filters.gender;
  if (filters.has_email !== undefined) out["has_email"] = filters.has_email;
  if (filters.has_phone !== undefined) out["has_phone"] = filters.has_phone;
  if (filters.only_deleted) out["only_deleted"] = true;
  if (filters.site) out["site"] = filters.site;
  if (filters.organisation) out["organisation"] = filters.organisation;
  if (filters.date_from) out["date_from"] = filters.date_from;
  if (filters.date_to) out["date_to"] = filters.date_to;
  if (filters.page) out["page"] = filters.page;
  if (filters.page_size) out["page_size"] = filters.page_size;
  if (filters.ordering) out["ordering"] = filters.ordering;
  return out;
};

export const patientsApi = {
  list: async (filters: PatientFilters = {}): Promise<PatientListResponse> => {
    const { data } = await api.get<PatientListResponse>("/patients/", {
      params: buildParams(filters),
    });
    return data;
  },
  get: async (id: string): Promise<Patient> => {
    const { data } = await api.get<Patient>(`/patients/${id}/`);
    return data;
  },
  create: async (payload: Partial<Patient>, idempotencyKey?: string): Promise<Patient> => {
    const { data } = await api.post<Patient>("/patients/", payload, withIdempotencyKey(idempotencyKey));
    return data;
  },
  update: async (id: string, payload: Partial<Patient>): Promise<Patient> => {
    const { data } = await api.patch<Patient>(`/patients/${id}/`, payload);
    return data;
  },
  remove: async (id: string): Promise<void> => {
    await api.delete(`/patients/${id}/`);
  },
  anonymize: async (id: string, confirmFullName: string): Promise<Patient> => {
    const { data } = await api.post<Patient>(`/patients/${id}/anonymize/`, {
      confirm_full_name: confirmFullName,
    });
    return data;
  },
  duplicates: async (id: string): Promise<Patient[]> => {
    const { data } = await api.get<Patient[]>(`/patients/${id}/duplicates/`);
    return data;
  },
  merge: async (sourceId: string, targetId: string): Promise<Patient> => {
    const { data } = await api.post<Patient>(`/patients/${sourceId}/merge/`, {
      target_id: targetId,
    });
    return data;
  },
  restore: async (id: string): Promise<Patient> => {
    const { data } = await api.post<Patient>(`/patients/${id}/restore/`, {});
    return data;
  },
  releaseCode: async (id: string): Promise<ReleasedCode> => {
    const { data } = await api.post<ReleasedCode>(`/patients/${id}/release-code/`, {});
    return data;
  },
  purge: async (id: string, confirmFullName: string): Promise<void> => {
    await api.post(`/patients/${id}/purge/`, { confirm_full_name: confirmFullName });
  },
  transferTargets: async (id: string): Promise<TransferTargets> => {
    const { data } = await api.get<TransferTargets>(`/patients/${id}/transfer-targets/`);
    return data;
  },
  transfer: async (id: string, organisationId: string): Promise<Patient> => {
    const { data } = await api.post<Patient>(`/patients/${id}/transfer/`, {
      organisation_id: organisationId,
    });
    return data;
  },
  // Gestes de masse sur la corbeille. Les filtres sont ceux de l'écran : le
  // back les relit et impose `only_deleted`, pour que « tout » désigne
  // exactement les fiches affichées, jamais davantage.
  restoreAll: async (filters: PatientFilters = {}): Promise<BulkRestore> => {
    const { data } = await api.post<BulkRestore>(
      "/patients/restore-all/",
      {},
      { params: buildParams({ ...filters, only_deleted: true }) },
    );
    return data;
  },
  releaseAllCodes: async (filters: PatientFilters = {}): Promise<BulkReleasedCodes> => {
    const { data } = await api.post<BulkReleasedCodes>(
      "/patients/release-codes/",
      {},
      { params: buildParams({ ...filters, only_deleted: true }) },
    );
    return data;
  },
  purgeAll: async (filters: PatientFilters = {}, confirm: string): Promise<BulkPurge> => {
    const { data } = await api.post<BulkPurge>(
      "/patients/purge-all/",
      { confirm },
      { params: buildParams({ ...filters, only_deleted: true }) },
    );
    return data;
  },
  summary: async (id: string): Promise<PatientSummary> => {
    const { data } = await api.get<PatientSummary>(`/patients/${id}/summary/`);
    return data;
  },
  getMedicalRecord: async (id: string): Promise<PatientMedicalRecord> => {
    const { data } = await api.get<PatientMedicalRecord>(`/patients/${id}/medical-record/`);
    return data;
  },
  updateMedicalRecord: async (
    id: string,
    payload: Omit<PatientMedicalRecord, "updated_at">,
  ): Promise<PatientMedicalRecord> => {
    const { data } = await api.put<PatientMedicalRecord>(
      `/patients/${id}/medical-record/`,
      payload,
    );
    return data;
  },
  nextCode: async (): Promise<NextPatientCode> => {
    const { data } = await api.get<NextPatientCode>("/patients/next-code/");
    return data;
  },
};

/** Une clinique d'accueil possible, et la raison si elle ne l'est pas. */
export interface TransferTarget {
  id: string;
  name: string;
  slug: string;
  available: boolean;
  reason: string | null;
}

/**
 * Tout ce dont la fenêtre de transfert a besoin, en un appel. `moves` ne dépend
 * pas de la destination — c'est la même descendance quelle que soit l'agence.
 * `blocked` renseigné = aucun transfert possible, quelle que soit la cible.
 */
export interface TransferTargets {
  blocked: string | null;
  moves: Record<string, number>;
  targets: TransferTarget[];
}

/** Réponse de « Libérer le N° » : le numéro rendu disponible. */
export interface ReleasedCode {
  released_code: string;
  patient: Patient;
}

/** Réponse de « Tout restaurer » : nombre de fiches sorties de la corbeille. */
export interface BulkRestore {
  restored: number;
}

/**
 * Réponse de « Tout libérer » : les N° rendus disponibles, et le nombre de
 * fiches passées parce que leur N° avait déjà été libéré.
 */
export interface BulkReleasedCodes {
  released: string[];
  skipped: number;
}

/**
 * Réponse de « Vider la corbeille » : fiches effacées, et fiches gardées en
 * corbeille parce qu'elles portent des actes.
 */
export interface BulkPurge {
  purged: number;
  kept: number;
}

export interface NextPatientCode {
  last_value: number;
  /** Dernier matricule attribué (null si aucun patient). */
  last_code: string | null;
  /** Prochain matricule si laissé vide. */
  next_code: string;
  /** Format de la série de la clinique, imposé à la saisie manuelle. */
  prefix: string;
  digits: number;
}
